import React from "react";
import { cn } from "@/lib/utils";
import {
  notificationSeverityTokens,
  type NotificationSeverityName,
} from "@/lib/tokens";

export interface PageBannerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: NotificationSeverityName;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  onDismiss?: () => void;
  dismissLabel?: string;
}

export const PageBanner: React.FC<PageBannerProps> = ({
  tone = "info",
  title,
  description,
  icon,
  actions,
  className,
  onDismiss,
  dismissLabel = "Dismiss",
  ...rest
}) => {
  const severity = notificationSeverityTokens[tone];

  return (
    <section
      className={cn(
        "rounded-md border px-4 py-3 sm:px-5 sm:py-4",
        "flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4",
        className,
      )}
      style={{
        backgroundColor: severity.surface,
        borderColor: severity.border,
        color: severity.text,
      }}
      role="status"
      aria-live="polite"
      {...rest}
    >
      {icon ? (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/60">
          {icon}
        </div>
      ) : null}
      <div className="flex-1 min-w-0 space-y-1">
        <h3 className="text-sm font-semibold tracking-tight text-slate-900">
          {title}
        </h3>
        {description ? (
          <p className="text-sm leading-relaxed text-slate-700">{description}</p>
        ) : null}
        {actions ? <div className="mt-2 flex flex-wrap gap-2">{actions}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          className="text-sm font-medium text-slate-600 hover:text-slate-900"
          onClick={onDismiss}
        >
          {dismissLabel}
        </button>
      ) : null}
    </section>
  );
};

PageBanner.displayName = "PageBanner";
